<template>
  <main
    class="mx-auto mb-5 pb-5 mb-md-0 pb-md-0"
    :class="{ narrowMain: restrictWidth }"
  >
    <div v-if="showAdminBar" class="container-fluid skin-body">
      <div class="row mt-2">
        <div class="col-lg-8 mx-auto">
          <admin-bar />
        </div>
      </div>
    </div>

    <top-bar v-if="showTopBar" />

    <div class="container-fluid skin-body mb-5">
      <div class="row">
        <bad-response-viewer ref="badResponseViewer"
          messagePrefix="Please refresh the page. Unable to recover your latest information because: " />

        <user-home v-if="showUserHome" />

        <you v-if="showYou" />

        <requirements v-if="showRequirements" />

        <DBOptions v-if="showDBOptions" />

        <income v-if="showIncome" />

        <life v-if="showLife" />

        <design v-if="showDesign" />

        <test v-if="showTest" />

        <finish v-if="showFinish" />

        <options v-if="showOptions" />

        <dashboard v-if="showDashboard" />

        <guiide-plus v-if="showGuiidePlus" />

        <support v-if="showSupport" />
      </div>
    </div>

    <Footer v-if="showFooter" :padding-top="0" :margin-top="0"></Footer>
  </main>
</template>

<script lang="ts">
import '@vuepic/vue-datepicker/dist/main.css';
import { defineComponent, ref } from "vue";
import { app } from "@/guiide/ts/app";
import View from "@/guiide/ts/view";
import AdminBar from "@/guiide/components/admin/AdminBar.vue";
import TopBar from "@/guiide/components/common/TopBar.vue";
import BadResponseViewer from "../shared/components/BadResponseViewer.vue";
import UserHome from "@/guiide/components/UserHome.vue";
import You from "@/guiide/components/You.vue";
import Requirements from "@/guiide/components/Requirements.vue";
import DBOptions from "@/guiide/components/DBOptions.vue";
import Income from "@/guiide/components/Income.vue";
import Life from "@/guiide/components/Life.vue";
import Design from "@/guiide/components/Design.vue";
import Test from "@/guiide/components/Test.vue";
import Finish from "@/guiide/components/Finish.vue";
import Options from "@/guiide/components/Options.vue";
import Dashboard from "@/guiide/components/Dashboard.vue";
import GuiidePlus from "@/guiide/components/GuiidePlus.vue";
import Footer from './components/common/Footer.vue';
import ResponseTracker from "@/shared/ts/responseTracker";
import { AxiosResponse } from "axios";
import { isAdminUser } from "./ts/utilities";
import Support from "@/guiide/components/Support.vue";

export default defineComponent({
  name: "App",
  components: {
    AdminBar,
    TopBar,
    BadResponseViewer,
    UserHome,
    You,
    Requirements,
    DBOptions,
    Income,
    Life,
    Design,
    Test,
    Finish,
    Options,
    Dashboard,
    GuiidePlus,
    Footer,
    Support
  },
  setup() {
    const badResponseViewer = ref(null);

    return {
      badResponseViewer
    };
  },
  async created() {
    await app.initialise();
    const getRetireeResponseTracker = this.getRetireeResponse;
    if (
      !getRetireeResponseTracker.isOk &&
      getRetireeResponseTracker.trackerWillHandleFailure
    ) {
      getRetireeResponseTracker.handleFailure();
    }
  },
  computed: {
    showTopBar() {
      return this.$store.state.view != View.GuiidePlus;
    },
    showAdminBar() {
      return isAdminUser();
    },
    showUserHome() {
      return this.$store.state.view == View.UserHome;
    },
    showYou() {
      return this.$store.state.view == View.You;
    },
    showRequirements() {
      return this.$store.state.view == View.Requirements;
    },
    showDBOptions() {
      return this.$store.state.view == View.DBOptions;
    },
    showIncome() {
      return this.$store.state.view == View.Haves;
    },
    showLife() {
      return this.$store.state.view == View.Life;
    },
    showDesign() {
      return this.$store.state.view == View.Design;
    },
    showTest() {
      return this.$store.state.view == View.Test;
    },
    showFinish() {
      return this.$store.state.view == View.Finish;
    },
    showOptions() {
      return this.$store.state.view == View.Options;
    },
    showDashboard() {
      return this.$store.state.view == View.Dashboard;
    },
    showGuiidePlus() {
      return this.$store.state.view == View.GuiidePlus;
    },
    showSupport() {
      return this.$store.state.view == View.Support;
    },
    showFooter() {
      return this.$store.state.configuration?.footer?.visibleInAuthApp ?? false;
    },
    getRetireeResponse(): null | ResponseTracker<AxiosResponse<any>> {
      return this.$store.state.getRetireeResponse;
    },
    restrictWidth() {
      return this.$store.state.view != View.GuiidePlus;
    }
  },
  watch: {
    getRetireeResponse(
      newResponse: null | ResponseTracker<AxiosResponse<any>>
    ) {
      if (newResponse != null) {
        if (newResponse.trackerWillHandleFailure) {
          newResponse.handleFailure();
        } else {
          this.badResponseViewer.update(newResponse.isOk, newResponse.message);
        }
      }
    }
  }
});
</script>

<style>
    .narrowMain {
      max-width: 1440px;
    }

    h2 {
      line-height: 1.2;
      margin-bottom: 16px;
    }

    .table th {
      font-size: 14px;
    }
</style>
